import Button from "react-bootstrap/Button";
import DropdownButton from "react-bootstrap/DropdownButton";
import Dropdown from "react-bootstrap/Dropdown";

import LoadingSpinner from "../LoadingSpinner";

import { useTranslation } from "react-i18next";

interface WhereOperator {
  eq: string | number | boolean | null
  ne: string | number | boolean | null
  in: string[] | number[] | boolean[] | null
  like: string | null
  gt: string | number | null
  gte: string | number | null
  lt: string | number | null
  lte: string | number | null
}

type WhereArgument = Record<string, Partial<WhereOperator> | Array<Record<string, Partial<WhereOperator>>>> | null | undefined

type PaginationProps = {
  pageLoading: boolean;
  paginationState: {
    pageNumber: number;
    pageSize: number;
    where: WhereArgument
  };
  handleNextPage: (pageSize: number, pageNumber: number, where: WhereArgument) => void;
  handlePrevPage: (pageSize: number, pageNumber: number, where: WhereArgument) => void;
  handlePageSize?: (pageSize: number, pageNumber: number, where: WhereArgument) => void;
  hasNextPage: boolean | undefined;
  totalCount?: number | undefined;
  disablePageSize?: boolean;
  enableInfinitePagination?: boolean;
  pageSizeOptions?: number[];
  paddingBottom?: string;
}

export default function Pagination({
  pageLoading,
  paginationState,
  handleNextPage,
  handlePrevPage,
  handlePageSize,
  hasNextPage,
  totalCount,
  disablePageSize = false,
  enableInfinitePagination = false,
  pageSizeOptions = [16, 32, 64, 100],
  paddingBottom = "3rem"
}: PaginationProps) {
  const { t } = useTranslation();
  const totalPages = (totalCount ?? 0) > 0 ? Math.ceil((totalCount ?? 0) / paginationState.pageSize) : 1;
  const pageText = enableInfinitePagination
    ? t("core.ui.pagination_infinite", {
      defaultValue: `Page ${paginationState.pageNumber}`,
      page: paginationState.pageNumber
    })
    : t("core.ui.pagination", {
      defaultValue: `Page ${paginationState.pageNumber} of ${totalPages}`,
      page: paginationState.pageNumber,
      pages: totalPages
    });
  const showText = (count: number) => t("core.ui.show", {
    count,
    defaultValue: `Show ${count}`
  });
  const nextPageDisabled = pageLoading
    || hasNextPage === false
    || (hasNextPage == null && totalCount != null && paginationState.pageNumber >= totalPages);

  return (
    <div 
      className="d-flex justify-content-between align-items-center flex-wrap row-gap-3" 
      style={{paddingBottom: paddingBottom}}
    >
      <div className="d-flex align-items-center">
        <Button
          variant="secondary"
          className="me-1"
          onClick={() => handlePrevPage(
            paginationState.pageSize,
            paginationState.pageNumber - 1,
            paginationState.where ?? undefined)}
          disabled={(paginationState.pageNumber <= 1) || pageLoading}
        >
          {"<"}
        </Button>
        <Button
          variant="secondary"
          className="me-1"
          onClick={() => handleNextPage(
            paginationState.pageSize,
            paginationState.pageNumber + 1,
            paginationState.where ?? undefined)}
          disabled={nextPageDisabled}
        >
          {">"}
        </Button>
        <p className="mb-0 mx-2 text-dark">
          {pageText}
        </p>
        { pageLoading && <LoadingSpinner/> }
      </div>
      { disablePageSize === false && handlePageSize != null
        && <div className="d-flex justify-content-md-end">
          <DropdownButton
            className="w-auto"
            aria-label="Page size"
            id="show-chargers"
            title={showText(paginationState.pageSize)}
            variant="secondary"
            onSelect={e => handlePageSize(
              parseInt(e ?? "0"),
              paginationState.pageNumber,
              paginationState.where ?? undefined) }
          >
            {pageSizeOptions.map(pageSize => (
              <Dropdown.Item key={pageSize} eventKey={pageSize}>
                {showText(pageSize)}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>  
      }
    </div>
  );
}
